/**
 * Pure formatters for a member row's status pill, medal line, lives suffix
 * and role label. No React import, no SDK import, so a Bun test can drive
 * every state directly.
 *
 * `contestStatus` is produced by `/member/list` (ISSUE-685) and carries a
 * fourth state, `notPlaying`, for a member outside the active contest or
 * with no active contest at all, and a fifth state, `unavailable`, for a
 * member pool-api could not get an answer for because contest-api was
 * unreachable or refused (ISSUE-706). `notPlaying` is a value contest-api
 * produces; pool-api never manufactures it, so it never means "we don't
 * know". `livesLeft` and the medal and trophy counts remain optional
 * because no service produces them yet. A row whose contestStatus is
 * `notPlaying` or `unavailable`, or that carries none of the contest
 * fields, renders no pill, never a placeholder derived from the membership
 * status alone.
 */

export type typeMemberContestStatus =
  "in" | "eliminated" | "revived" | "notPlaying" | "unavailable";

export type typeMemberPillTone = "success" | "accent" | "warning" | "error";

export type typeMemberPill = {
  label: string;
  tone: typeMemberPillTone;
};

export function memberStatusPill(input: {
  membershipStatus:
    "pending" | "active" | "suspended" | "left" | "removed" | "rejected";
  contestStatus?: typeMemberContestStatus | null;
  livesLeft?: number | null;
}): typeMemberPill | null {
  const { membershipStatus, contestStatus, livesLeft } = input;

  if (membershipStatus === "pending") {
    return { label: "Pending", tone: "warning" };
  }
  if (membershipStatus === "suspended") {
    return { label: "Suspended", tone: "warning" };
  }
  if (contestStatus === "eliminated") {
    return { label: "Eliminated", tone: "error" };
  }

  let base: string | null = null;
  let tone: typeMemberPillTone | null = null;
  if (contestStatus != null) {
    switch (contestStatus) {
      case "revived":
        base = "Revived";
        tone = "accent";
        break;
      case "in":
        base = "In";
        tone = "success";
        break;
      case "notPlaying":
        // A member who is not playing has no contest standing to show:
        // no pill, never a placeholder derived from the membership status
        // alone.
        break;
      case "unavailable":
        // pool-api could not reach contest-api, so this member's standing
        // is not known. No pill, and never notPlaying's pill either,
        // because we are not claiming the member is out of the contest.
        // The pane shows one banner for the whole list. ISSUE-706.
        break;
      default: {
        const exhaustiveCheck: never = contestStatus;
        return exhaustiveCheck;
      }
    }
  }

  if (base === null || tone === null) return null;

  if (typeof livesLeft === "number" && livesLeft >= 0) {
    const unit = livesLeft === 1 ? "life" : "lives";
    return { label: `${base} · ${livesLeft} ${unit} left`, tone };
  }

  return { label: base, tone };
}

export function memberMedalLine(input: {
  medalsThisSeason?: number | null;
  medalsAwardedThisSeason?: number | null;
  trophiesThisSeason?: number | null;
}): string | null {
  const { medalsThisSeason, medalsAwardedThisSeason, trophiesThisSeason } =
    input;

  if (typeof medalsThisSeason !== "number") return null;

  const awarded = medalsAwardedThisSeason ?? 0;
  const medalWord = medalsThisSeason === 1 ? "medal" : "medals";
  let line = `${medalsThisSeason} ${medalWord} · ${awarded} awarded`;

  if (typeof trophiesThisSeason === "number" && trophiesThisSeason > 0) {
    const trophyWord = trophiesThisSeason === 1 ? "trophy" : "trophies";
    line = `${line} · ${trophiesThisSeason} ${trophyWord}`;
  }

  return line;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;
const WEEK_MS = 604_800_000;

export function askedAgoLabel(input: {
  createdAtIso: string;
  nowMs: number;
}): string {
  const { createdAtIso, nowMs } = input;
  const createdMs = Date.parse(createdAtIso);

  if (Number.isNaN(createdMs) || createdMs >= nowMs) {
    return "Asked just now";
  }

  const diff = nowMs - createdMs;

  if (diff < MINUTE_MS) return "Asked just now";

  if (diff < HOUR_MS) {
    const n = Math.floor(diff / MINUTE_MS);
    return `Asked ${n} ${n === 1 ? "minute" : "minutes"} ago`;
  }

  if (diff < DAY_MS) {
    const n = Math.floor(diff / HOUR_MS);
    return `Asked ${n} ${n === 1 ? "hour" : "hours"} ago`;
  }

  if (diff < WEEK_MS) {
    const n = Math.floor(diff / DAY_MS);
    return `Asked ${n} ${n === 1 ? "day" : "days"} ago`;
  }

  const n = Math.floor(diff / WEEK_MS);
  return `Asked ${n} ${n === 1 ? "week" : "weeks"} ago`;
}

export function roleLabel(
  poolRole: "owner" | "admin" | "member",
): string | null {
  if (poolRole === "owner") return "Commissioner";
  if (poolRole === "admin") return "Admin";
  return null;
}
